import { useEffect, useRef } from 'react';
import { Circle, Popup, Tooltip, useMap } from 'react-leaflet';
import type { LatLngExpression, Circle as LeafletCircle } from 'leaflet';
import type { Metro } from '../../types/metro';
import { ratioColor, ratioRadius } from '../../lib/map-utils';
import { useIsMobile } from '../../lib/use-is-mobile';
import { useMetroStore } from '../../stores/metroStore';
import { useFilterStore } from '../../stores/filterStore';
import { lensedRatioForMetro } from '../../lib/occupation-utils';
import { MetroPopup } from './MetroPopup';
import { MetroTooltip } from './MetroTooltip';
import { METRO_POPUP_WIDTH_PX } from '../../lib/layout-offsets';

interface MetroBubbleProps {
  readonly metro: Metro;
  readonly isSelected: boolean;
  readonly onSelect: (id: string) => void;
}

/**
 * One bubble per metro. Uses Leaflet's `<Circle>` (geographic radius in
 * meters) per locked scope decision Q2.A — bubbles scale with zoom level,
 * matching `_archive/map.html`'s `L.circle({ radius: factor * 1200 })`
 * pattern (lines 807-816).
 *
 * Per-bubble styling matches prototype lines 813-816:
 *   stroke:      white (#fff) — gives bubbles a halo against the map tiles
 *   weight:      1.5 (constant — selection highlight is via a thicker
 *                stroke and the popup chrome, not a stroke change)
 *   fillColor:   ratioColor(metro.ratio) — continuous gradient
 *   fillOpacity: 0.75 (selected: 0.9 — slightly more opaque for emphasis)
 *
 * Hover renders the rich `<MetroTooltip>` (replaces the old basic span).
 * Click opens the `<MetroPopup>` with sector breakdown + stats grid.
 *
 * Selection effect: when `isSelected` flips to true (from sidebar click
 * OR direct bubble click), the map flies to this metro's coordinates
 * and opens its popup. Sidebar-driven selection is the primary use
 * case — researchers scan the list, click a row, and expect the map
 * to follow. Bubble click also triggers the effect, which is fine:
 * `flyTo` to a coordinate the user is already looking at is
 * essentially a no-op (Leaflet skips the animation when source ≈
 * target).
 *
 * Zoom preserves user intent: if they've already zoomed in deeper
 * than 7, we keep their zoom and just pan. If they're at the default
 * 4 (national view), we zoom in to 7 so the metro is clearly
 * visible. Popup opens after a 650ms delay so it attaches AFTER the
 * 600ms flyTo settles (otherwise it re-anchors awkwardly mid-pan).
 */
export function MetroBubble({ metro, isSelected, onSelect }: MetroBubbleProps) {
  const center: LatLngExpression = [metro.lat, metro.lng];
  // 2026-07-03 (V2 feedback follow-up): when the user has picked a
  // specific occupational group from the sidebar filter, the bubble
  // should reflect THAT group's ratio (color + size), not the
  // metro-wide ratio. Reviewers pointed out that filtering to
  // "Healthcare" while the bubble still shows the metro's overall
  // ratio hides the very question they were trying to answer.
  //
  // CR-06 (2026-07-08): the lookup used to be inlined here AND
  // duplicated in MetroTooltip + MetroPopup (which still rendered the
  // overall ratio in their headlines under an OG filter — the client's
  // "bubbles still show the overall ratio" complaint, GX5). Extracted
  // into the shared `lensedRatioForMetro()` helper so all three
  // components read the same lensing logic instead of drifting.
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const { ratio: effectiveRatio } = lensedRatioForMetro(metro, occupationalGroupId);
  // 2026-05-18 feedback: continuous red->green ratio scale (no
  // 'balanced' band) + size driven by ratio (was totalJobs). Severe
  // shortages render with the largest, reddest bubbles for emphasis.
  const fill = ratioColor(effectiveRatio);
  const map = useMap();
  const circleRef = useRef<LeafletCircle | null>(null);
  // IMP-005 (mobile popup pass, 2026-06): on mobile the Leaflet popup
  // is suppressed entirely because its 560px body gets clipped inside
  // the 50vh map container and the close button often lands off-screen
  // (Maria 2026-06-05 screenshot). The MapMobileLayout owns a
  // full-viewport modal that takes over as the metro-detail surface
  // and watches `useMetroStore.selectedMetroId` to know when to open.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isSelected) return;
    const targetZoom = Math.max(map.getZoom(), 7);
    map.flyTo([metro.lat, metro.lng], targetZoom, { duration: 0.6 });
    // Open the Leaflet popup AFTER the flyTo settles. Skip entirely
    // on mobile — the mobile modal is driven directly off the
    // selection state in the page-level layout, not the Leaflet
    // popup chrome.
    if (isMobile) return undefined;
    // `?.openPopup?.()` so the tests' react-leaflet mock (which
    // renders Circle as a plain div with no Leaflet methods on the
    // ref) is a safe no-op rather than throwing.
    const t = window.setTimeout(() => {
      circleRef.current?.openPopup?.();
    }, 650);
    return () => window.clearTimeout(t);
    // Cleanup pass: deps include `metro.id` (per code-reviewer
    // verdict). If a data refresh re-binds this bubble to a different
    // metro while still selected, the effect refires for the new
    // coords. Lat/lng read fresh inside via `metro.lat/lng` rather
    // than a stale closure. `map` is excluded — it's a stable Leaflet
    // instance for the lifetime of the MapContainer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, metro.id, isMobile]);

  return (
    <Circle
      ref={circleRef}
      center={center}
      radius={ratioRadius(effectiveRatio)}
      pathOptions={{
        color: '#ffffff',
        weight: 1.5,
        fillColor: fill,
        fillOpacity: isSelected ? 0.9 : 0.75,
        opacity: 0.9,
      }}
      eventHandlers={{
        click: () => onSelect(metro.id),
      }}
    >
      <Tooltip direction="top" offset={[0, -4]} opacity={1} sticky>
        <MetroTooltip metro={metro} />
      </Tooltip>
      {/* Desktop only — on mobile the page-level modal owns the
          metro-detail surface (see MapMobileLayout).

          Viewport-clamp options (Maria Slack 2026-06-05 / B1):
          Leaflet popups anchor to a map coordinate, so when the user
          pans or zooms the underlying point off-screen the popup
          follows it and disappears from view. The fix uses the two
          framework-native flags:
            - autoPan: true   — on open, pan the map so the popup
                                is fully visible (Leaflet default,
                                made explicit for clarity).
            - keepInView: true — while the popup is open, if the
                                user pans toward an edge that would
                                clip the popup, Leaflet smoothly
                                nudges the camera to keep it
                                visible. This is the load-bearing
                                flag for Maria's bug — without it,
                                the popup escapes the viewport on
                                pan/zoom even though it was anchored
                                in-bounds when opened.
          autoPanPaddingTopLeft / autoPanPaddingBottomRight give the
          popup 20px breathing room from each viewport edge so it
          isn't visually flush against the chrome. */}
      {!isMobile && (
        <Popup
          minWidth={METRO_POPUP_WIDTH_PX}
          maxWidth={METRO_POPUP_WIDTH_PX}
          closeButton={false}
          autoPan
          keepInView
          autoPanPaddingTopLeft={[20, 20]}
          autoPanPaddingBottomRight={[20, 20]}
        >
          {/* 2026-07-03 (V2 feedback follow-up): the expand icon
              promotes the compact Leaflet popup into a full-viewport
              modal at the MapExplorer level. We close the Leaflet
              popup here first so the two don't stack; the modal
              takes over rendering. */}
          <MetroPopup
            metro={metro}
            onClose={() => map.closePopup()}
            onExpand={() => {
              useMetroStore.getState().setPopupExpanded(true);
              map.closePopup();
            }}
          />
        </Popup>
      )}
    </Circle>
  );
}

